import { env } from '$lib/config/environment';
import { authHeader } from '$lib/stores/auth';
import { endpointNeedsSession } from '$lib/config/mode';
import { Log } from '@ainova/core';

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiRequestOptions extends RequestInit {
  timeout?: number;
  retries?: number;
  retryDelay?: number;
}

export interface ApiResponse<T> {
  data: T;
  status: number;
  headers: Headers;
}

/**
 * The signed-in customer's credential, on every request.
 *
 * # Why it is here and not at the call sites
 *
 * A selection is fetched by a dozen modules — filters, count, prompt, lists,
 * neighbours, similarity — and what a signed-in customer gets back depends on
 * the API seeing this header. Opting in per call means every one of them has
 * to remember, and the failure mode when one forgets is silent: the request
 * succeeds, the rows come back masked, and the customer is looking at
 * obfuscated data while signed in with no error anywhere. That is exactly what
 * happened before this existed.
 *
 * Attaching it centrally is safe because this client only ever talks to our own
 * API — `buildUrl` resolves every endpoint against `env.apiBaseUrl` — so the
 * token cannot leak to a third party through it.
 *
 * `authHeader()` returns nothing for a visitor and nothing once the session has
 * lapsed, so an anonymous request stays anonymous and a stale token is not sent
 * to be refused.
 */
class ApiClient {
  private logger = new Log(env.features.debugging ? Log.DEBUG : Log.INFO);
  private baseUrl: string;
  private defaultTimeout: number;

  constructor() {
    this.baseUrl = env.apiBaseUrl;
    this.defaultTimeout = env.apiTimeout;
  }

  private async fetchWithTimeout(url: string, options: ApiRequestOptions = {}): Promise<Response> {
    const { timeout = this.defaultTimeout, ...fetchOptions } = options;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...fetchOptions,
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new ApiError('Request timeout', 408);
      }
      throw error;
    }
  }

  /**
   * Refuse, in the members' build, to call the API with no session.
   *
   * # Why here rather than at the call sites
   *
   * The same reason the token is attached here: there is one chokepoint and a
   * dozen callers, and a caller that forgets fails silently. `get`, `post`,
   * `put`, `delete` and `upload` all pass through this, so a method added
   * later inherits the guard instead of having to remember it.
   *
   * # Why it throws rather than returning empty
   *
   * Because a request with no token is not refused by the API — it is answered
   * with the visitor's masked rows and a 200. Swallowing this would put those
   * rows on a member's screen, which is exactly the confusion the two builds
   * exist to remove. A caller that is not expecting to be signed out should
   * see something go wrong.
   *
   * It is a 401 because that is what the request was going to be worth.
   */
  private refuseWithoutSession(url: string): void {
    if (env.mode !== 'member') return;

    // Asked of `authHeader` rather than of the session store, so that what
    // counts as having a credential is decided in one place. It returns
    // nothing for a visitor and nothing once a session has lapsed.
    if (Object.keys(authHeader()).length > 0) return;

    const path = new URL(url).pathname;
    if (!endpointNeedsSession(path)) return;

    this.logger.warn(`Refusing ${path}: the members' app has no session`);
    throw new ApiError('Not signed in', 401, { code: 'NO_SESSION' });
  }

  private async fetchWithRetry(url: string, options: ApiRequestOptions = {}): Promise<Response> {
    // Before the retry loop: a refusal is not a failure to retry, and three
    // attempts at a request that never leaves the browser is three of nothing.
    this.refuseWithoutSession(url);

    const { retries = 3, retryDelay = 1000, ...fetchOptions } = options;

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const response = await this.fetchWithTimeout(url, fetchOptions);

        // Handle rate limiting (429)
        if (response.status === 429) {
          const retryAfter = this.getRetryAfterDelay(response);
          const delay = retryAfter || Math.min(retryDelay * Math.pow(2, attempt), 60000); // Exponential backoff, max 60s

          this.logger.warn(
            `Rate limited (attempt ${attempt + 1}/${retries + 1}): waiting ${delay}ms before retry`
          );

          if (attempt < retries) {
            await new Promise((resolve) => setTimeout(resolve, delay));
            continue;
          } else {
            throw new ApiError('Rate limit exceeded', 429, { retryAfter: delay });
          }
        }

        // Don't retry on other client errors (4xx)
        if (response.status >= 400 && response.status < 500) {
          return response;
        }

        // Retry on server errors (5xx) or network errors
        if (response.ok || attempt === retries) {
          return response;
        }

        this.logger.warn(
          `Request failed (attempt ${attempt + 1}/${retries + 1}): ${response.status}`
        );

        if (attempt < retries) {
          await new Promise((resolve) => setTimeout(resolve, retryDelay * (attempt + 1)));
        }
      } catch (error) {
        this.logger.error(`Request error (attempt ${attempt + 1}/${retries + 1}):`, error);

        if (attempt === retries) {
          throw error;
        }

        await new Promise((resolve) => setTimeout(resolve, retryDelay * (attempt + 1)));
      }
    }

    throw new ApiError('Max retries exceeded', 503);
  }

  private getRetryAfterDelay(response: Response): number | null {
    const retryAfter = response.headers.get('Retry-After');
    if (!retryAfter) return null;

    // Check if it's a number (seconds) or a date
    const seconds = parseInt(retryAfter, 10);
    if (!isNaN(seconds)) {
      return seconds * 1000; // Convert to milliseconds
    }

    // Try to parse as date
    const retryDate = new Date(retryAfter);
    if (!isNaN(retryDate.getTime())) {
      const delay = retryDate.getTime() - Date.now();
      return delay > 0 ? delay : null;
    }

    return null;
  }

  private buildUrl(endpoint: string, params?: Record<string, unknown>): string {
    const url = new URL(endpoint, this.baseUrl);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    const finalUrl = url.toString();
    return finalUrl;
  }

  private async parseResponse<T>(response: Response): Promise<ApiResponse<T>> {
    let data: T;

    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      try {
        data = await response.json();
      } catch (error) {
        this.logger.error('Failed to parse JSON response:', error);
        throw new ApiError('Invalid JSON response', response.status);
      }
    } else {
      data = (await response.text()) as unknown as T;
    }

    if (!response.ok) {
      throw new ApiError(`API request failed: ${response.statusText}`, response.status, data);
    }

    return {
      data,
      status: response.status,
      headers: response.headers
    };
  }

  async get<T>(
    endpoint: string,
    params?: Record<string, unknown>,
    options?: ApiRequestOptions
  ): Promise<ApiResponse<T>> {
    const url = this.buildUrl(endpoint, params);

    this.logger.debug(`GET ${url}`);

    const response = await this.fetchWithRetry(url, {
      ...options,
      method: 'GET',
      headers: {
        Accept: 'application/json',
        ...authHeader(),
        ...options?.headers
      }
    });

    return this.parseResponse<T>(response);
  }

  async post<T>(
    endpoint: string,
    data?: unknown,
    options?: ApiRequestOptions
  ): Promise<ApiResponse<T>> {
    const url = this.buildUrl(endpoint);

    this.logger.debug(`POST ${url}`, data);

    const response = await this.fetchWithRetry(url, {
      ...options,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...authHeader(),
        ...options?.headers
      },
      body: data ? JSON.stringify(data) : undefined
    });

    return this.parseResponse<T>(response);
  }

  /**
   * Post a file.
   *
   * Separate from `post` because the two disagree about the body: `post`
   * serialises to JSON and declares the content type, and an upload must do
   * neither — the browser sets `multipart/form-data` with the boundary it
   * generated, and a Content-Type header written by hand has no boundary in it,
   * which the server reads as a malformed request.
   */
  async upload<T>(
    endpoint: string,
    form: FormData,
    options?: ApiRequestOptions
  ): Promise<ApiResponse<T>> {
    const url = this.buildUrl(endpoint);

    this.logger.debug(`POST ${url} (multipart)`);

    const response = await this.fetchWithRetry(url, {
      ...options,
      method: 'POST',
      headers: {
        Accept: 'application/json',
        ...authHeader(),
        ...options?.headers
      },
      body: form
    });

    return this.parseResponse<T>(response);
  }

  async put<T>(
    endpoint: string,
    data?: unknown,
    options?: ApiRequestOptions
  ): Promise<ApiResponse<T>> {
    const url = this.buildUrl(endpoint);

    this.logger.debug(`PUT ${url}`, data);

    const response = await this.fetchWithRetry(url, {
      ...options,
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...authHeader(),
        ...options?.headers
      },
      body: data ? JSON.stringify(data) : undefined
    });

    return this.parseResponse<T>(response);
  }

  /**
   * Change part of a resource.
   *
   * Added for the saved lists, which the API exposes as PATCH. Written out
   * rather than routed through `put` because the two are different requests to
   * the server: gin matches on the method, and a PUT to a PATCH route is a 404
   * — a failure that reads like a wrong path rather than a wrong verb.
   */
  async patch<T>(
    endpoint: string,
    data?: unknown,
    options?: ApiRequestOptions
  ): Promise<ApiResponse<T>> {
    const url = this.buildUrl(endpoint);

    this.logger.debug(`PATCH ${url}`, data);

    const response = await this.fetchWithRetry(url, {
      ...options,
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...authHeader(),
        ...options?.headers
      },
      body: data ? JSON.stringify(data) : undefined
    });

    return this.parseResponse<T>(response);
  }

  /**
   * Delete, optionally saying what to delete.
   *
   * `data` exists for the one route that needs it: removing records from a
   * saved list names them in the body, because a list of identifiers does not
   * belong in a path and the subresource is the whole point — see
   * docs/refactor/saved-lists.md.
   *
   * The content type is only declared when there is a body. A DELETE announcing
   * JSON it is not sending is a request some proxies will wait on for a body
   * that never arrives.
   */
  async delete<T>(
    endpoint: string,
    data?: unknown,
    options?: ApiRequestOptions
  ): Promise<ApiResponse<T>> {
    const url = this.buildUrl(endpoint);

    this.logger.debug(`DELETE ${url}`, data);

    const response = await this.fetchWithRetry(url, {
      ...options,
      method: 'DELETE',
      headers: {
        ...(data === undefined ? {} : { 'Content-Type': 'application/json' }),
        Accept: 'application/json',
        ...authHeader(),
        ...options?.headers
      },
      body: data === undefined ? undefined : JSON.stringify(data)
    });

    return this.parseResponse<T>(response);
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
